import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ArrowLeft, Plus, Trash2, DollarSign } from "lucide-react";
import { DateCell } from "@/components/DateCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface ContractLine {
  id: string;
  contractId: string;
  productId: string;
  price: string | null;
  costPlusPct: string | null;
  priceUom: string;
}

interface Contract {
  id: string;
  name: string;
  type: string | null;
  contractType: string | null;
  gpoName: string | null;
  value: string | null;
  status: string;
  startDate: string;
  endDate: string;
  validFrom: string | null;
  validTo: string | null;
  documentPath: string | null;
  partyId: string | null;
  siteUseId: string | null;
  account?: { id: string; name: string };
  lines: ContractLine[];
}

interface Product {
  id: string;
  name: string;
}

export default function ContractDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/contracts/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newLine, setNewLine] = useState({ productId: "", price: "", costPlusPct: "", priceUom: "EA" });

  const id = params?.id;

  const { data: contract, isLoading } = useQuery<Contract>({
    queryKey: ["/api/contracts", id],
    queryFn: () => api.get<Contract>(`/contracts/${id}`, sessionId),
    enabled: !!id,
  });

  const { data: productsData } = useQuery<{ products: Product[]; total: number }>({
    queryKey: ["/api/products", { limit: 200 }],
    queryFn: () => api.get<{ products: Product[]; total: number }>(`/products?limit=200`, sessionId),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<Contract>) => api.patch<Contract>(`/contracts/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts", id] });
      toast({ title: "Contract updated" });
    },
  });

  const addLineMutation = useMutation({
    mutationFn: (payload: { productId: string; price?: string; costPlusPct?: string; priceUom: string }) =>
      api.post<ContractLine>(`/contracts/${id}/lines`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts", id] });
      setNewLine({ productId: "", price: "", costPlusPct: "", priceUom: "EA" });
      toast({ title: "Contract line added" });
    },
  });

  const deleteLineMutation = useMutation({
    mutationFn: (lineId: string) => api.delete(`/contract-lines/${lineId}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts", id] });
      toast({ title: "Contract line removed" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!contract) {
    return <div className="p-8 text-center text-muted-foreground">Contract not found</div>;
  }

  const products = productsData?.products || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={contract.name}
        description={`${contract.account?.name || "No account"}${contract.contractType ? ` · ${contract.contractType.replace(/_/g, " ")}` : ""}`}
        actions={
          <Button variant="outline" onClick={() => setLocation("/contracts")} data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        }
      />

      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm" data-testid="section-contract-commercial">
        <div className="px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            Contract · Commercial
          </h2>
        </div>
        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Status</p>
              <Select
                value={contract.status}
                onValueChange={(val) => updateMutation.mutate({ status: val })}
              >
                <SelectTrigger data-testid="select-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Contract Type</p>
              <Select
                value={contract.contractType || ""}
                onValueChange={(val) => updateMutation.mutate({ contractType: val as any })}
              >
                <SelectTrigger data-testid="select-contract-type-trigger">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIXED_PRICE">Fixed Price</SelectItem>
                  <SelectItem value="COST_PLUS">Cost Plus</SelectItem>
                  <SelectItem value="GPO">GPO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Valid Period</p>
              <p className="text-sm pt-2">
                <DateCell value={contract.validFrom || contract.startDate} fallback="N/A" />
                {" — "}
                <DateCell value={contract.validTo || contract.endDate} fallback="N/A" />
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
            <div>
              <p className="text-xs text-muted-foreground">GPO Name</p>
              <p className="text-sm font-medium" data-testid="text-gpo-name">
                {contract.gpoName || <span className="text-muted-foreground italic">-</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Document</p>
              <p className="text-sm font-mono truncate" data-testid="text-document-path">
                {contract.documentPath || <span className="text-muted-foreground italic">-</span>}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Contract Lines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Cost Plus %</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(contract.lines || []).map((line) => {
                const product = products.find((p) => p.id === line.productId);
                return (
                  <TableRow key={line.id} data-testid={`row-contract-line-${line.id}`}>
                    <TableCell className="font-medium">{product?.name || line.productId}</TableCell>
                    <TableCell>{line.price ? `$${Number(line.price).toFixed(2)}` : "-"}</TableCell>
                    <TableCell>{line.costPlusPct ? `${line.costPlusPct}%` : "-"}</TableCell>
                    <TableCell>{line.priceUom || "EA"}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteLineMutation.mutate(line.id)}
                        data-testid={`button-delete-line-${line.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!contract.lines || contract.lines.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No contract lines yet. Add products below.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4 flex gap-2 items-end border-t pt-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Product</label>
              <Select value={newLine.productId} onValueChange={(v) => setNewLine({ ...newLine, productId: v })}>
                <SelectTrigger data-testid="select-new-line-product">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Price</label>
              <Input
                type="number"
                step="any"
                min={0}
                placeholder="0.00"
                value={newLine.price}
                onChange={(e) => setNewLine({ ...newLine, price: e.target.value })}
                data-testid="input-new-line-price"
              />
            </div>
            <div className="w-28">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Cost+ %</label>
              <Input
                type="number"
                step="any"
                min={0}
                max={99.9999}
                placeholder="0"
                value={newLine.costPlusPct}
                onChange={(e) => setNewLine({ ...newLine, costPlusPct: e.target.value })}
                data-testid="input-new-line-cost-plus"
              />
            </div>
            <Button
              onClick={() => {
                if (!newLine.productId) return;
                addLineMutation.mutate({
                  productId: newLine.productId,
                  price: newLine.price || undefined,
                  costPlusPct: newLine.costPlusPct || undefined,
                  priceUom: newLine.priceUom,
                });
              }}
              disabled={!newLine.productId || addLineMutation.isPending}
              data-testid="button-add-line"
            >
              {addLineMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Line
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
